/**
 * Component: sa-scroll-reveal
 * Description: Scroll triggered text reveal section
 * Structure: Line > Word > Inner Text
 */
if (!customElements.get('sa-scroll-reveal')) {
  class SaScrollReveal extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      this.textElement = this.querySelector('.sa-scroll-reveal__text');
      if (!this.textElement) return;

      // Wait for next frame to ensure layout is ready
      requestAnimationFrame(() => {
        this.splitText();
        this.words = Array.from(this.querySelectorAll('.sa-scroll-reveal__word-inner'));
        
        // Group words by line for staggered animation
        const lines = Array.from(this.querySelectorAll('.sa-scroll-reveal__line'));
        this.linesWithWords = lines.map(line => ({
          words: Array.from(line.querySelectorAll('.sa-scroll-reveal__word-inner'))
        }));

        this.updateWords = this.updateWords.bind(this);
        
        // Setup initial height
        this.setupLayout();
        window.addEventListener('resize', this.setupLayout.bind(this));
        
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.updateWords(); // Initial check
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.setupLayout);
    }

    setupLayout() {
        if (!this.words || this.words.length === 0) return;
        
        // Configuration
        this.stagger = 100; // Pixel delay per word (Faster sequence)
        this.duration = 600; // Animation duration in pixels (Faster rise)
        // Start animation BEFORE the section hits the top (negative buffer).
        // This ensures words are already visible when sticky lock happens.
        this.startBuffer = -window.innerHeight * 0.3; 
        this.endBuffer = window.innerHeight * 1.2; // Slightly reduced end buffer
        
        // Calculate total scrollable height needed
        // Last word starts at: startBuffer + (N-1)*stagger
        // Last word ends at: startBuffer + (N-1)*stagger + duration
        // Total track needed: Last word end time + endBuffer
        
        const lastWordEndTime = this.startBuffer + ((this.words.length - 1) * this.stagger) + this.duration;
        const totalAnimationDistance = lastWordEndTime + this.endBuffer;
        
        // The container height needs to be: Animation Distance + 1 Viewport Height (for the sticky element itself)
        this.style.height = `${totalAnimationDistance + window.innerHeight}px`;
    }

    splitText() {
      // Structure: <span class="line"> <span class="word"> <span>Text</span> </span> </span>
      const originalContent = this.textElement.innerHTML;
      // Split by <br> to get lines
      const lines = originalContent.split(/<br\s*\/?>/i);
      
      const wrappedContent = lines.map(line => {
        // Remove extra whitespace and split by space
        const words = line.trim().split(/\s+/);
        
        const wrappedWords = words.map(word => {
          if (!word) return '';
          // Inner span is for the text content, outer .word is for the container/mask if needed
          return `<span class="sa-scroll-reveal__word"><span class="sa-scroll-reveal__word-inner" style="display: inline-block; will-change: transform, opacity;">${word}</span></span>`;
        }).join(' ');

        return `<span class="sa-scroll-reveal__line">${wrappedWords}</span>`;
      }).join(''); // Join lines without <br> because lines are blocks now

      this.textElement.innerHTML = wrappedContent;
    }

    handleScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.updateWords();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    updateWords() {
      if (!this.words) return;

      // Calculate how far we have scrolled into the section
      // .top is negative when we scroll down past the top edge
      const containerRect = this.getBoundingClientRect();
      const scrolledPixels = -containerRect.top;
      
      // If we are before the section (scrolledPixels < 0), or just entered
      // The sticky logic handles the visual pinning.
      // We just map scrolledPixels to the animation timeline.
      
      let globalWordIndex = 0;

      this.linesWithWords.forEach(lineObj => {
        lineObj.words.forEach((word) => {
          
          // Determine when this specific word should start animating
          const wordStartScroll = this.startBuffer + (globalWordIndex * this.stagger);
          
          // Current progress for this word
          // distance = how far past the start point are we?
          const distance = scrolledPixels - wordStartScroll;
          
          let progress = distance / this.duration;
          progress = Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1

          // Easing (Cubic ease out)
          const ease = 1 - Math.pow(1 - progress, 3);
          
          // Apply styles
          // Start: translate3d(0, 300%, 0) opacity: 0
          // End: translate3d(0, 0, 0) opacity: 1
          const translateY = (1 - ease) * 300;
          
          word.style.transform = `translate3d(0px, ${translateY}%, 0px)`;
          word.style.opacity = progress;
          
          // Toggle active class
          if (progress >= 0.8) {
            word.classList.add('sa-scroll-reveal__word-inner--active');
          } else {
            word.classList.remove('sa-scroll-reveal__word-inner--active');
          }
          
          globalWordIndex++;
        });
      });
    }
  }

  customElements.define('sa-scroll-reveal', SaScrollReveal);
}
